import { ensureTransaction } from './data';

/**
 * Transaction process for all bookings over 90 days in advance
 */

/**
 * Transitions
 *
 * These strings must sync with values defined in Flex API,
 * since transaction objects given by API contain info about last transitions.
 * All the actions in API side happen in transitions,
 * so we need to understand what those strings mean.
 */

export const TRANSITION_REQUEST_BOOKING = 'transition/request-booking';
export const TRANSITION_ACCEPT = 'transition/accept';
export const TRANSITION_AUTO_ACCEPT = 'transition/auto-accept';
export const TRANSITION_DECLINE = 'transition/decline';
export const TRANSITION_EXPIRE = 'transition/expire';
export const TRANSITION_NO_REPLY_EXPIRE = 'transition/no-reply-expire';
export const TRANSITION_MARK_PAYMENT_PENDING = 'transition/mark-payment-pending';
export const TRANSITION_INITIATE_PAYMENT = 'transition/initiate-payment';
export const TRANSITION_CANCEL_PAYMENT_PENDING = 'transition/cancel-payment-pending';
export const TRANSITION_PROVIDER_CANCEL_PAYMENT_PENDING =
  'transition/provider-cancel-payment-pending';
export const TRANSITION_CUSTOMER_CANCEL_PAYMENT_PENDING =
  'transition/customer-cancel-payment-pending';
export const TRANSITION_PAYMENT_PROCESSING = 'transition/payment-processing';
export const TRANSITION_CANCEL_PROCESSED_PAYMENT = 'transition/cancel-processed-payment';
export const TRANSITION_PROVIDER_CANCEL_PROCESSED_PAYMENT =
  'transition/provider-cancel-processed-payment';
export const TRANSITION_CUSTOMER_CANCEL_PROCESSED_PAYMENT =
  'transition/customer-cancel-processed-payment';
export const TRANSITION_PAYMENT_SUCCEEDING = 'transition/payment-succeeding';
export const TRANSITION_PAYMENT_FAILING = 'transition/payment-failing';
export const TRANSITION_INITIATE_PAYMENT_INTENT = 'transition/initiate-payment-intent';
export const TRANSITION_CONFIRM_PAYMENT_INTENT = 'transition/confirm-payment-intent';
export const TRANSITION_EXPIRE_PAYMENT_INTENT = 'transition/expire-payment-intent';
export const TRANSITION_EXPIRE_INITIATE_PAYMENT_INTENT =
  'transition/expire-initiate-payment-intent';
export const TRANSITION_CANCEL_PAYED_PAYMENT = 'transition/cancel-payed-payment';
export const TRANSITION_PROVIDER_CANCEL_PAYED_PAYMENT = 'transition/provider-cancel-payed-payment';
export const TRANSITION_CUSTOMER_CANCEL_PAYED_PAYMENT = 'transition/customer-cancel-payed-payment';
export const TRANSITION_COMPLETE = 'transition/complete';
export const TRANSITION_REVIEW_1_BY_PROVIDER = 'transition/review-1-by-provider';
export const TRANSITION_REVIEW_2_BY_PROVIDER = 'transition/review-2-by-provider';
export const TRANSITION_REVIEW_1_BY_CUSTOMER = 'transition/review-1-by-customer';
export const TRANSITION_REVIEW_2_BY_CUSTOMER = 'transition/review-2-by-customer';

/**
 * Actors
 *
 * There are 4 different actors that might initiate transitions:
 */

// Roles of actors that perform transaction transitions
export const TX_TRANSITION_ACTOR_CUSTOMER = 'customer';
export const TX_TRANSITION_ACTOR_PROVIDER = 'provider';
export const TX_TRANSITION_ACTOR_SYSTEM = 'system';
export const TX_TRANSITION_ACTOR_OPERATOR = 'operator';

export const TX_TRANSITION_ACTORS = [
  TX_TRANSITION_ACTOR_CUSTOMER,
  TX_TRANSITION_ACTOR_PROVIDER,
  TX_TRANSITION_ACTOR_SYSTEM,
  TX_TRANSITION_ACTOR_OPERATOR,
];

/**
 * States
 *
 * These constants are only for making it clear how transitions work together.
 * You should not use these constants outside of this file.
 *
 * Note: these states are not in sync with states used transaction process definitions
 *       in Marketplace API. Only last transitions are passed along transaction object.
 */
const STATE_INITIAL = 'initial';
const STATE_PREAUTHORIZED = 'preauthorized';
const STATE_ACCEPTED = 'accepted';
const STATE_DECLINED = 'declined';
const STATE_PAYMENT_PENDING = 'payment-pending';
const STATE_INITIATED_PAYMENT = 'initiated-payment';
const STATE_INITIATED_PAYMENT_INTENT = 'initiated-payment-intent';
const STATE_PROCESSED_PAYMENT = 'processed-payment';
const STATE_EXPIRED_PAYMENT_INTENT = 'expired-payment-intent';
const STATE_FAILED_PAYMENT = 'payment-failed';
const STATE_PAYED = 'payed';
const STATE_CANCELED = 'canceled';
const STATE_DELIVERED = 'delivered';
const STATE_REVIEWED = 'reviewed';
const STATE_REVIEWED_BY_CUSTOMER = 'reviewed-by-customer';
const STATE_REVIEWED_BY_PROVIDER = 'reviewed-by-provider';

/**
 * Description of transaction process
 *
 * You should keep this in sync with transaction process defined in Marketplace API
 *
 * Note: we don't use yet any state machine library,
 *       but this description format is following Xstate (FSM library)
 *       https://xstate.js.org/docs/
 */
export const delayedStateDescription = {
  // id is defined only to support Xstate format.
  // However if you have multiple transaction processes defined,
  // it is best to keep them in sync with transaction process aliases.
  id: 'delayed-nightly-booking/release-2',

  // This 'initial' state is a starting point for new transaction
  initial: STATE_INITIAL,

  // States
  states: {
    [STATE_INITIAL]: {
      on: {
        [TRANSITION_REQUEST_BOOKING]: STATE_PREAUTHORIZED,
      },
    },
    [STATE_PREAUTHORIZED]: {
      on: {
        [TRANSITION_ACCEPT]: STATE_ACCEPTED,
        [TRANSITION_AUTO_ACCEPT]: STATE_ACCEPTED,
        [TRANSITION_DECLINE]: STATE_DECLINED,
        [TRANSITION_EXPIRE]: STATE_DECLINED,
        [TRANSITION_NO_REPLY_EXPIRE]: STATE_DECLINED,
      },
    },
    [STATE_ACCEPTED]: {
      on: {
        [TRANSITION_MARK_PAYMENT_PENDING]: STATE_PAYMENT_PENDING,
      },
    },
    [STATE_PAYMENT_PENDING]: {
      // wait until 90 days before end of booking
      on: {
        [TRANSITION_INITIATE_PAYMENT]: STATE_INITIATED_PAYMENT,
        [TRANSITION_CANCEL_PAYMENT_PENDING]: STATE_CANCELED,
        [TRANSITION_PROVIDER_CANCEL_PAYMENT_PENDING]: STATE_CANCELED,
        [TRANSITION_CUSTOMER_CANCEL_PAYMENT_PENDING]: STATE_CANCELED,
      },
    },
    [STATE_INITIATED_PAYMENT]: {
      on: {
        [TRANSITION_PAYMENT_PROCESSING]: STATE_PROCESSED_PAYMENT,
      },
    },
    [STATE_PROCESSED_PAYMENT]: {
      // init stripe payment intent
      on: {
        [TRANSITION_PAYMENT_SUCCEEDING]: STATE_PAYED, // payment intent successful
        [TRANSITION_PAYMENT_FAILING]: STATE_FAILED_PAYMENT, // payment intent failed
        [TRANSITION_CANCEL_PROCESSED_PAYMENT]: STATE_CANCELED,
        [TRANSITION_PROVIDER_CANCEL_PROCESSED_PAYMENT]: STATE_CANCELED,
        [TRANSITION_CUSTOMER_CANCEL_PROCESSED_PAYMENT]: STATE_CANCELED,
      },
    },
    [STATE_FAILED_PAYMENT]: {
      on: {
        [TRANSITION_INITIATE_PAYMENT_INTENT]: STATE_INITIATED_PAYMENT_INTENT, // init manuel payment
        [TRANSITION_EXPIRE_INITIATE_PAYMENT_INTENT]: STATE_EXPIRED_PAYMENT_INTENT,
      },
    },
    [STATE_INITIATED_PAYMENT_INTENT]: {
      on: {
        [TRANSITION_CONFIRM_PAYMENT_INTENT]: STATE_PAYED,
        [TRANSITION_EXPIRE_PAYMENT_INTENT]: STATE_EXPIRED_PAYMENT_INTENT,
      },
    },
    [STATE_PAYED]: {
      on: {
        [TRANSITION_COMPLETE]: STATE_DELIVERED,
        [TRANSITION_CANCEL_PAYED_PAYMENT]: STATE_CANCELED,
        [TRANSITION_PROVIDER_CANCEL_PAYED_PAYMENT]: STATE_CANCELED,
        [TRANSITION_CUSTOMER_CANCEL_PAYED_PAYMENT]: STATE_CANCELED,
      },
    },
    [STATE_DECLINED]: {},
    [STATE_CANCELED]: {},
    [STATE_DELIVERED]: {
      on: {
        [TRANSITION_REVIEW_1_BY_CUSTOMER]: STATE_REVIEWED_BY_CUSTOMER,
        [TRANSITION_REVIEW_1_BY_PROVIDER]: STATE_REVIEWED_BY_PROVIDER,
      },
    },
    [STATE_REVIEWED_BY_CUSTOMER]: {
      on: {
        [TRANSITION_REVIEW_2_BY_PROVIDER]: STATE_REVIEWED,
      },
    },
    [STATE_REVIEWED_BY_PROVIDER]: {
      on: {
        [TRANSITION_REVIEW_2_BY_CUSTOMER]: STATE_REVIEWED,
      },
    },
    [STATE_REVIEWED]: { type: 'final' },
  },
};

// Note: currently we assume that state description doesn't contain nested states.
const statesFromStateDescription = (description) => description.states || {};

// Get all the transitions from states object in an array
const getTransitions = (states) => {
  const stateNames = Object.keys(states);

  const transitionsReducer = (transitionArray, name) => {
    const stateTransitions = states[name] && states[name].on;
    const transitionKeys = stateTransitions ? Object.keys(stateTransitions) : [];
    return [
      ...transitionArray,
      ...transitionKeys.map((key) => ({ key, value: stateTransitions[key] })),
    ];
  };

  return stateNames.reduce(transitionsReducer, []);
};

// This is a list of all the transitions that this app should be able to handle.
export const TRANSITIONS = getTransitions(statesFromStateDescription(delayedStateDescription)).map(
  (t) => t.key
);

// This function returns a function that has given stateDesc in scope chain.
const getTransitionsToStateFn = (stateDesc) => (state) =>
  getTransitions(statesFromStateDescription(stateDesc))
    .filter((t) => t.value === state)
    .map((t) => t.key);

// Get all the transitions that lead to specified state.
const getTransitionsToState = getTransitionsToStateFn(delayedStateDescription);

// This is needed to fetch transactions that need response from provider.
// I.e. transactions which provider needs to accept or decline
export const transitionsToRequestedForProvider = getTransitionsToState([
  STATE_PREAUTHORIZED,
  STATE_REVIEWED_BY_CUSTOMER,
]);
export const transitionsToRequestedForCustomer = getTransitionsToState([
  STATE_REVIEWED_BY_PROVIDER,
  STATE_FAILED_PAYMENT,
]);

/**
 * Helper functions to figure out if transaction is in a specific state.
 * State is based on lastTransition given by transaction object and state description.
 */

const txLastTransition = (tx) => ensureTransaction(tx).attributes.lastTransition;

export const txIsPendingBooking = (tx) =>
  getTransitionsToState(STATE_PREAUTHORIZED).includes(txLastTransition(tx));

export const txIsAccepted = (tx) =>
  getTransitionsToState(STATE_ACCEPTED).includes(txLastTransition(tx));

export const txIsPaymentPending = (tx) =>
  getTransitionsToState(STATE_PAYMENT_PENDING).includes(txLastTransition(tx));

export const txIsInitiatedPayment = (tx) =>
  getTransitionsToState(STATE_INITIATED_PAYMENT).includes(txLastTransition(tx));

export const txIsProcessedPayment = (tx) =>
  getTransitionsToState(STATE_PROCESSED_PAYMENT).includes(txLastTransition(tx));

export const txIsInitiatedPaymentIntent = (tx) =>
  getTransitionsToState(STATE_INITIATED_PAYMENT_INTENT).includes(txLastTransition(tx));

export const txIsExpiredPaymentIntent = (tx) =>
  getTransitionsToState(STATE_EXPIRED_PAYMENT_INTENT).includes(txLastTransition(tx));

export const txIsFailedPayment = (tx) =>
  getTransitionsToState(STATE_FAILED_PAYMENT).includes(txLastTransition(tx));

export const txIsPayed = (tx) => getTransitionsToState(STATE_PAYED).includes(txLastTransition(tx));

export const txIsDeclined = (tx) =>
  getTransitionsToState(STATE_DECLINED).includes(txLastTransition(tx));

export const txIsCanceled = (tx) =>
  getTransitionsToState(STATE_CANCELED).includes(txLastTransition(tx));

export const txIsDelivered = (tx) =>
  getTransitionsToState(STATE_DELIVERED).includes(txLastTransition(tx));

const firstReviewTransitions = [
  ...getTransitionsToState(STATE_REVIEWED_BY_CUSTOMER),
  ...getTransitionsToState(STATE_REVIEWED_BY_PROVIDER),
];

export const txIsReviewedByCustomer = (tx) =>
  getTransitionsToState(STATE_REVIEWED_BY_CUSTOMER).includes(txLastTransition(tx));

export const txIsReviewedByProvider = (tx) =>
  getTransitionsToState(STATE_REVIEWED_BY_PROVIDER).includes(txLastTransition(tx));

export const txIsInFirstReviewBy = (tx, isCustomer) =>
  isCustomer
    ? getTransitionsToState(STATE_REVIEWED_BY_CUSTOMER).includes(txLastTransition(tx))
    : getTransitionsToState(STATE_REVIEWED_BY_PROVIDER).includes(txLastTransition(tx));

export const txIsReviewed = (tx) => firstReviewTransitions.includes(txLastTransition(tx));

export const txIsProviderCanceled = (tx) =>
  [
    TRANSITION_PROVIDER_CANCEL_PAYMENT_PENDING,
    TRANSITION_PROVIDER_CANCEL_PROCESSED_PAYMENT,
    TRANSITION_PROVIDER_CANCEL_PAYED_PAYMENT,
  ].includes(tx.attributes.lastTransition);

export const txIsCustomerCanceled = (tx) =>
  [
    TRANSITION_CUSTOMER_CANCEL_PAYMENT_PENDING,
    TRANSITION_CUSTOMER_CANCEL_PROCESSED_PAYMENT,
    TRANSITION_CUSTOMER_CANCEL_PAYED_PAYMENT,
  ].includes(tx.attributes.lastTransition);

export const txIsOperatorCanceled = (tx) =>
  [
    TRANSITION_CANCEL_PAYMENT_PENDING,
    TRANSITION_CANCEL_PROCESSED_PAYMENT,
    TRANSITION_CANCEL_PAYED_PAYMENT,
  ].includes(tx.attributes.lastTransition);

/**
 * Helper functions to figure out if transaction has passed a given state.
 * This is based on transitions history given by transaction object.
 */

const txTransitions = (tx) => ensureTransaction(tx).attributes.transitions || [];
const hasPassedTransition = (transitionName, tx) =>
  !!txTransitions(tx).find((t) => t.transition === transitionName);

const hasPassedStateFn = (state) => (tx) =>
  getTransitionsToState(state).filter((t) => hasPassedTransition(t, tx)).length > 0;

export const txHasBeenAccepted = hasPassedStateFn(STATE_ACCEPTED);
export const txHasBeenPayed = hasPassedStateFn(STATE_PAYED);
export const txHasBeenDelivered = hasPassedStateFn(STATE_DELIVERED);

/**
 * Other transaction related utility functions
 */

export const transitionIsReviewed = (transition) =>
  getTransitionsToState(STATE_REVIEWED).includes(transition);

export const getReview1Transition = (isCustomer) =>
  isCustomer ? TRANSITION_REVIEW_1_BY_CUSTOMER : TRANSITION_REVIEW_1_BY_PROVIDER;

export const getReview2Transition = (isCustomer) =>
  isCustomer ? TRANSITION_REVIEW_2_BY_CUSTOMER : TRANSITION_REVIEW_2_BY_PROVIDER;

// Check if a transition is the kind that should be rendered
// when showing transition history (e.g. ActivityFeed)
// The first transition and most of the expiration transitions made by system are not relevant
export const isRelevantPastTransition = (transition) => {
  return [
    TRANSITION_REQUEST_BOOKING,
    TRANSITION_ACCEPT,
    TRANSITION_AUTO_ACCEPT,
    TRANSITION_DECLINE,
    TRANSITION_EXPIRE,
    TRANSITION_MARK_PAYMENT_PENDING,
    TRANSITION_CANCEL_PAYMENT_PENDING,
    TRANSITION_PROVIDER_CANCEL_PAYMENT_PENDING,
    TRANSITION_CUSTOMER_CANCEL_PAYMENT_PENDING,
    TRANSITION_PAYMENT_PROCESSING,
    TRANSITION_CANCEL_PROCESSED_PAYMENT,
    TRANSITION_PROVIDER_CANCEL_PROCESSED_PAYMENT,
    TRANSITION_CUSTOMER_CANCEL_PROCESSED_PAYMENT,
    TRANSITION_PAYMENT_SUCCEEDING,
    TRANSITION_PAYMENT_FAILING,
    TRANSITION_CONFIRM_PAYMENT_INTENT,
    TRANSITION_EXPIRE_INITIATE_PAYMENT_INTENT,
    TRANSITION_CANCEL_PAYED_PAYMENT,
    TRANSITION_PROVIDER_CANCEL_PAYED_PAYMENT,
    TRANSITION_CUSTOMER_CANCEL_PAYED_PAYMENT,
    TRANSITION_COMPLETE,
    // To show review response
    TRANSITION_REVIEW_1_BY_CUSTOMER,
    TRANSITION_REVIEW_1_BY_PROVIDER,

    // To show reviews where made
    TRANSITION_REVIEW_2_BY_CUSTOMER,
    TRANSITION_REVIEW_2_BY_PROVIDER,
  ].includes(transition);
};

export const isCustomerReview = (transition) => {
  return [TRANSITION_REVIEW_1_BY_CUSTOMER, TRANSITION_REVIEW_2_BY_CUSTOMER].includes(transition);
};

export const isProviderReview = (transition) => {
  return [TRANSITION_REVIEW_1_BY_PROVIDER, TRANSITION_REVIEW_2_BY_PROVIDER].includes(transition);
};

export const getUserTxRole = (currentUserId, transaction) => {
  const tx = ensureTransaction(transaction);
  const customer = tx.customer;
  if (currentUserId && currentUserId.uuid && tx.id && customer.id) {
    // user can be either customer or provider
    return currentUserId.uuid === customer.id.uuid
      ? TX_TRANSITION_ACTOR_CUSTOMER
      : TX_TRANSITION_ACTOR_PROVIDER;
  } else {
    throw new Error(`Parameters for "userIsCustomer" function were wrong.
      currentUserId: ${currentUserId}, transaction: ${transaction}`);
  }
};

export const txRoleIsProvider = (userRole) => userRole === TX_TRANSITION_ACTOR_PROVIDER;
export const txRoleIsCustomer = (userRole) => userRole === TX_TRANSITION_ACTOR_CUSTOMER;

// Check if the given transition is privileged.
//
// Privileged transitions need to be handled from a secure context,
// i.e. the backend. This helper is used to check if the transition
// should go through the local API endpoints, or if using JS SDK is
// enough.
export const isPrivileged = (transition) => {
  return [
    TRANSITION_REQUEST_BOOKING,
    TRANSITION_INITIATE_PAYMENT_INTENT,
    TRANSITION_PAYMENT_PROCESSING,
  ].includes(transition);
};
